/**
 * PurchaseCommercialAddOnModal — purchase staff-configured commercial add-ons via subscription invoice.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { IconPackage } from "@tabler/icons-react";
import {
  getCommercialAddOnCatalog,
  getCreditBundleInvoiceStatus,
  purchaseCommercialAddOn,
  type CommercialAddOnOffering,
} from "../api";

function formatUsd(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

/** Strip request IDs and "Card error:" prefix for display. */
function formatPaymentErrorMessage(
  raw: string | null | undefined,
  defaultMessage: string
): string {
  if (!raw || typeof raw !== "string") return defaultMessage;
  let text = raw
    .replace(/^Card error:\s*/i, "")
    .replace(/Request req_\w+:\s*/gi, "")
    .trim();
  if (!text) return defaultMessage;
  const lower = text.toLowerCase();
  if (lower.includes("declined"))
    return "Your card was declined. Please try a different card or contact your bank.";
  if (lower.includes("insufficient funds"))
    return "Your card has insufficient funds. Please try a different card.";
  if (text.length > 120) return defaultMessage;
  return text;
}

async function waitForInvoicePaid(
  invoiceId: string,
  maxMs = 45000
): Promise<{ ok: boolean; status: string }> {
  const start = Date.now();
  while (Date.now() - start < maxMs) {
    const { status } = await getCreditBundleInvoiceStatus(invoiceId);
    if (status === "paid") return { ok: true, status };
    if (status === "uncollectible" || status === "void") {
      return { ok: false, status };
    }
    await new Promise((r) => setTimeout(r, 1000));
  }
  return { ok: false, status: "timeout" };
}

type Phase = "idle" | "loading_catalog" | "submitting" | "error";

interface PurchaseCommercialAddOnModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

export const PurchaseCommercialAddOnModal = ({
  opened,
  onClose,
  onSuccess,
}: PurchaseCommercialAddOnModalProps) => {
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);
  const [offerings, setOfferings] = useState<CommercialAddOnOffering[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const resetState = useCallback(() => {
    setPhase("idle");
    setError(null);
    setOfferings([]);
    setSelectedIds([]);
  }, []);

  useEffect(() => {
    if (!opened) return;
    resetState();
    let ignore = false;
    const load = async () => {
      setPhase("loading_catalog");
      try {
        const data = await getCommercialAddOnCatalog();
        if (ignore) return;
        const list = data.offerings ?? [];
        setOfferings(list);
        setSelectedIds([]);
        setPhase("idle");
      } catch {
        if (!ignore) {
          setPhase("error");
          setError("Could not load add-ons. Please try again.");
        }
      }
    };
    void load();
    return () => {
      ignore = true;
    };
  }, [opened, resetState]);

  const handleClose = useCallback(() => {
    if (phase === "submitting") return;
    resetState();
    onClose();
  }, [onClose, phase, resetState]);

  const handleSubmit = useCallback(async () => {
    if (selectedIds.length === 0 || phase === "submitting") return;
    setPhase("submitting");
    setError(null);
    try {
      for (const offeringId of selectedIds) {
        const requestId = crypto.randomUUID();
        const result = await purchaseCommercialAddOn(offeringId, requestId);
        if (result.status !== "paid") {
          const waited = await waitForInvoicePaid(result.invoiceId);
          if (!waited.ok) {
            setPhase("error");
            setError(
              waited.status === "timeout"
                ? "Payment is still processing. Refresh billing in a moment or check your email for the invoice."
                : "Payment did not complete. You can try again from Billing."
            );
            return;
          }
        }
      }
      await onSuccess();
      handleClose();
    } catch (err: unknown) {
      setPhase("idle");
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : null;
      setError(
        formatPaymentErrorMessage(
          msg,
          "Purchase failed. Please update your payment method in Billing and try again."
        )
      );
    }
  }, [handleClose, onSuccess, phase, selectedIds]);

  const isBusy = phase === "submitting" || phase === "loading_catalog";
  const hasPurchasableOfferings = offerings.some((o) => !o.alreadyPurchased);
  const selectedOfferings = offerings.filter((o) => selectedIds.includes(o.id));
  const selectedTotalAmountCents = selectedOfferings.reduce(
    (sum, offering) => sum + offering.amountCents,
    0
  );

  const handleConfirmPurchase = useCallback(() => {
    if (selectedIds.length === 0 || isBusy) return;
    const addOnCount = selectedOfferings.length;
    const amountLabel = formatUsd(selectedTotalAmountCents);
    modals.openConfirmModal({
      title: "Confirm add-on purchase",
      centered: true,
      labels: {
        confirm: `Charge ${amountLabel}`,
        cancel: "Cancel",
      },
      children: (
        <Stack gap={6}>
          <Text size="sm">
            You are about to purchase{" "}
            {addOnCount === 1 ? "this add-on" : `${addOnCount} add-ons`}.
          </Text>
          <Text size="sm">
            Your card on file will be charged {amountLabel}
            {addOnCount > 1 ? " across the selected add-ons." : "."}
          </Text>
        </Stack>
      ),
      onConfirm: () => {
        void handleSubmit();
      },
    });
  }, [
    handleSubmit,
    isBusy,
    selectedIds.length,
    selectedOfferings.length,
    selectedTotalAmountCents,
  ]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text size="xl" fw={600}>
          Add-on features
        </Text>
      }
      size="lg"
      closeOnClickOutside={!isBusy}
      closeOnEscape={!isBusy}
      radius="lg"
      padding="xl"
    >
      <Stack gap="md">
        <Text size="xs" c="dimmed">
          Select one or more add-ons. Each is charged separately to the card on
          file for your subscription.
        </Text>

        {phase === "loading_catalog" ? (
          <Loader size="sm" />
        ) : offerings.length === 0 ? (
          <Text size="sm">No add-ons are available right now.</Text>
        ) : !hasPurchasableOfferings ? (
          <Text size="sm">All available add-ons are already on your plan.</Text>
        ) : (
          <Checkbox.Group
            value={selectedIds}
            onChange={(ids) =>
              setSelectedIds(
                ids.filter((id) => {
                  const o = offerings.find((x) => x.id === id);
                  return o && !o.alreadyPurchased;
                })
              )
            }
            label="Choose add-ons"
          >
            <Stack gap="xs" mt="xs">
              {offerings.map((o) => {
                const imageSrc = (o.displayImageUrl ?? "").trim() || undefined;
                const locked = !!o.alreadyPurchased;
                return (
                  <Checkbox.Card
                    key={o.id}
                    value={o.id}
                    radius="md"
                    disabled={isBusy || locked}
                  >
                    <Group wrap="nowrap" align="flex-start" p="sm" gap="sm">
                      <Checkbox.Indicator />
                      <Avatar
                        src={imageSrc}
                        size={44}
                        radius="md"
                        color="blue"
                        variant="light"
                      >
                        <IconPackage size={22} stroke={1.5} />
                      </Avatar>
                      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={600}>
                          {o.displayTitle}
                        </Text>
                        {o.displayDescription ? (
                          <Text size="xs" c="dimmed">
                            {o.displayDescription}
                          </Text>
                        ) : null}
                        <Text size="sm">{formatUsd(o.amountCents)}</Text>
                        {locked ? (
                          <Text size="xs" c="dimmed" fw={500}>
                            Already on your contract
                          </Text>
                        ) : null}
                      </Stack>
                    </Group>
                  </Checkbox.Card>
                );
              })}
            </Stack>
          </Checkbox.Group>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Button
          onClick={handleConfirmPurchase}
          loading={phase === "submitting"}
          disabled={
            selectedIds.length === 0 ||
            offerings.length === 0 ||
            !hasPurchasableOfferings ||
            phase === "loading_catalog" ||
            phase === "submitting"
          }
        >
          {selectedIds.length > 1
            ? `Purchase ${selectedIds.length} add-ons`
            : "Purchase"}
        </Button>
      </Stack>
    </Modal>
  );
};
